import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { Box, type SxProps, type Theme, useTheme } from "@mui/material";
import { type FC, type KeyboardEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { DISABLED_CONTROL, MOTION, STEPPER } from "../../../theme/tokens";
import {
	buttonSx,
	fieldSx,
	revealSx,
	type SystemStepperControlsVisibility,
	type SystemStepperSize,
	type SystemStepperValueTone,
	stepperGapPx,
	valueSx,
} from "./systemStepperSx";

export { SYSTEM_STEPPER_REVEAL_CLASS, type SystemStepperControlsVisibility, type SystemStepperSize, type SystemStepperValueTone } from "./systemStepperSx";

type Props = {
	value: number;
	/** Called with the next value, already clamped to `[min, max]`. */
	onChange: (next: number) => void;
	min: number;
	max: number;
	/** The accessible name of the number ("How many shifts"). Already translated. */
	label: string;
	/**
	 * `large` makes the number the figure of its surface; `standard` sits in a form row. `compact` is one bordered field
	 * a compact control tall, its − and + inside it: for a form where the count is one figure among several (a staffing
	 * line's Target and Required), where round touch targets around every number would outweigh the form. `inline` is
	 * that field shrunk to sit in a dense table row (`CONTROL_HEIGHT.MICRO` tall, 24px buttons, a 26px value) — a row's
	 * figure that edits in place.
	 */
	size?: SystemStepperSize;
	/**
	 * `always` shows the − and + at rest. `reveal` keeps them invisible (and a field's border clear) until the stepper is
	 * hovered or focused, or an ancestor carrying `SYSTEM_STEPPER_REVEAL_CLASS` is — so a table of figures reads as
	 * figures until a row is pointed at. The buttons keep their width while hidden, so the number never moves. On a
	 * touch screen, which can't hover, they always show.
	 */
	controlsVisibility?: SystemStepperControlsVisibility;
	/** The number's ink: `brand` for a figure the surface reads a step quieter (a minimum beside its target). */
	valueTone?: SystemStepperValueTone;
	/** The value's tab stop: `-1` for a grid that moves focus between its cells itself (roving tabindex). */
	tabIndex?: 0 | -1;
	disabled?: boolean;
	/** On the root; `-decrement`, `-increment` and `-value` go on the parts. */
	dataTestId?: string;
	sx?: SxProps<Theme>;
};

/**
 * The stepper's width (px) at a size, while its value fits the value column (two digits): both buttons, the value and
 * the gaps between them (or a field's border). For a layout that has to know when a line of steppers fits, like a
 * container query.
 */
export const systemStepperWidthPx = (size: SystemStepperSize): number => {
	if (size === "compact") return 2 * STEPPER.COMPACT_BUTTON_WIDTH + STEPPER.VALUE_MIN_WIDTH.compact + 2 * STEPPER.COMPACT_BORDER;
	if (size === "inline") return 2 * STEPPER.INLINE_BUTTON_WIDTH + STEPPER.INLINE_VALUE_MIN_WIDTH + 2 * STEPPER.INLINE_BORDER;
	return 2 * STEPPER.BUTTON_SIZE + STEPPER.VALUE_MIN_WIDTH[size] + 2 * STEPPER.GAP_PX[size];
};

/** Keys the value answers to, as the WAI-ARIA spinbutton pattern lists them. */
const KEY_DELTA: Record<string, number> = { ArrowUp: 1, ArrowRight: 1, ArrowDown: -1, ArrowLeft: -1 };

/**
 * "How many" as − and + around the number — for a small count a person nudges (people to post, nurses still
 * needed), where a dropdown of 1–100 hides the answer behind a tap and a free number field summons a keyboard.
 *
 * The number itself is the control to assistive tech (`role="spinbutton"` with its value and bounds, arrow keys,
 * Home / End), and the buttons are its touch affordance, out of the tab order as the pattern prescribes. Each button
 * greys out at its end of the range rather than disappearing, so the row never reflows.
 *
 * A new value ticks in from the direction it moved — up when it grows, down when it shrinks — so a tap reads as a
 * change even when the eye is on the button, not the number. Movement is dropped under reduced motion; the fade stays.
 */
// DEFAULT: `size` absent is the form-row stepper, `controlsVisibility` absent shows the controls ("always"), `valueTone`
// absent is the page's ink, `tabIndex` absent keeps the value in the natural tab order (0), and `disabled` absent is
// enabled — each the plain case, not missing data.
export const SystemStepper: FC<Props> = ({
	value,
	onChange,
	min,
	max,
	label,
	size = "standard",
	controlsVisibility = "always",
	valueTone = "neutral",
	tabIndex = 0,
	disabled = false,
	dataTestId,
	sx,
}) => {
	const { t } = useTranslation();
	const theme = useTheme();
	// The direction of the last change, read by the tick. Derived from the previous value during render — React's pattern
	// for state that follows a prop — rather than written to a ref mid-render, which React may run twice or throw away.
	const [tick, setTick] = useState<{ value: number; direction: 1 | -1 }>({ value, direction: 1 });
	if (tick.value !== value) setTick({ value, direction: value > tick.value ? 1 : -1 });

	const set = (next: number): void => {
		const clamped = Math.min(max, Math.max(min, next));
		if (clamped !== value) onChange(clamped);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
		if (disabled) return;
		const delta = KEY_DELTA[event.key];
		if (delta !== undefined) set(value + delta);
		else if (event.key === "Home") set(min);
		else if (event.key === "End") set(max);
		else return;
		event.preventDefault();
	};

	const partSx = buttonSx(size, theme);

	return (
		<Box
			data-testid={dataTestId}
			sx={[
				{ display: "inline-flex", alignItems: "center", gap: `${stepperGapPx(size)}px`, opacity: disabled ? DISABLED_CONTROL.DIMMED_OPACITY : 1 },
				fieldSx(size, theme),
				...(controlsVisibility === "reveal" ? [revealSx(size)] : []),
				...(Array.isArray(sx) ? sx : [sx]),
			]}
		>
			<Box
				component="button"
				type="button"
				tabIndex={-1}
				aria-label={t("system.stepper.decrease", { label })}
				disabled={disabled || value <= min}
				onClick={() => set(value - 1)}
				data-testid={dataTestId === undefined ? undefined : `${dataTestId}-decrement`}
				sx={partSx}
			>
				<RemoveIcon />
			</Box>
			<Box
				role="spinbutton"
				tabIndex={disabled ? -1 : tabIndex}
				aria-label={label}
				aria-valuenow={value}
				aria-valuemin={min}
				aria-valuemax={max}
				aria-disabled={disabled || undefined}
				onKeyDown={handleKeyDown}
				data-testid={dataTestId === undefined ? undefined : `${dataTestId}-value`}
				sx={valueSx(size, valueTone, theme)}
			>
				<Box
					component="span"
					// Keyed by value so the tick replays on every change, and only then.
					key={value}
					sx={{
						display: "inline-block",
						// The render that adjusts `tick` is re-run before it commits, so the committed one always reads the new direction.
						"--stepper-rise": `${tick.direction * STEPPER.TICK_RISE_PX}px`,
						"@keyframes stepperTick": {
							from: { opacity: 0, transform: "translateY(var(--stepper-rise))" },
							to: { opacity: 1, transform: "translateY(0)" },
						},
						"@keyframes stepperFade": { from: { opacity: 0 }, to: { opacity: 1 } },
						animation: `stepperTick ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}`,
						"@media (prefers-reduced-motion: reduce)": { animation: `stepperFade ${MOTION.DURATION.CONTROL}ms ${MOTION.EASE_OUT}` },
					}}
				>
					{value}
				</Box>
			</Box>
			<Box
				component="button"
				type="button"
				tabIndex={-1}
				aria-label={t("system.stepper.increase", { label })}
				disabled={disabled || value >= max}
				onClick={() => set(value + 1)}
				data-testid={dataTestId === undefined ? undefined : `${dataTestId}-increment`}
				sx={partSx}
			>
				<AddIcon />
			</Box>
		</Box>
	);
};
